import { dispatch } from '@ngneat/effects';
import { createStore, emitOnce, select, withProps } from '@ngneat/elf';
import {
  getAllEntities,
  selectAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import {
  clearRequestsStatus,
  createRequestsStatusOperator,
  getRequestStatus,
  joinRequestResult,
  selectIsRequestPending,
  selectRequestStatus,
  updateRequestStatus,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import { trackRequest } from '@tmx/core/elf-utils';
import {
  DriverVehicleLink,
  FleetVehicleInformation,
  SmsInviteStatus,
  VehicleDriverLink,
} from '@tmx/data-access';
import {
  ClientOnlyScope,
  ClientOnlyValue,
  LoadingRowsDescriptor,
  RowDescriptor,
  RowsDescriptor,
  SelectAllState,
  SortDirection,
  UpdateRowsDescriptor,
} from '@tmx/operandi';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Subject,
} from 'rxjs';

import { FilterAssetsSelectOption } from '../../features/assets/components/filter-assets-dropdown/models/filter-assets-select-option';
import { AssetsImportResponse } from '../../models/assets-import-response';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import { HistoricalNotification } from '../../models/historical-notification';
import { LicenseUser } from '../../models/license-user';
import { refreshAssetsRowsDescriptor } from './assets.actions';

type RequestKeys = 'fetch-csv';

interface AssetProps {
  assetsRowsDescriptor: RowsDescriptor;
  copilotVersions: string[];
  filterAssetsSelectOption: FilterAssetsSelectOption | null;
  filterByAssetIds: string[];
  fleetInformationMap: Map<string, FleetVehicleInformation>;
  formattedMapVersions: Map<string, string>;
  hasAssets: boolean;
  historicalNotifications: HistoricalNotification[];
  importResponse: AssetsImportResponse;
  linkedDriverMap: Map<string, VehicleDriverLink>;
  linkedVehicleMap: Map<string, DriverVehicleLink>;
  offset: number;
  query: string;
  selectAllClientOnlyScope: ClientOnlyScope;
  selectAllClientOnlyValue: ClientOnlyValue;
  selectAllJobTraceId: null | string;
  selection: Map<string, LicenseUser>;
  singleAssetDriverData: null | VehicleDriverLink;
  smsStatusesMap: Map<string, SmsInviteStatus>;
  sortDirection: '' | 'asc' | 'desc';
  sortExpression: string;
  totalAssets: number;
  vehiclesHistoryRowsDescriptor: RowsDescriptor;
}

const assetStore = createStore(
  { name: 'asset' },
  withProps<AssetProps>({
    totalAssets: 0,
    hasAssets: false,
    offset: 0,
    sortExpression: '',
    sortDirection: SortDirection.None,
    query: '',
    filterAssetsSelectOption: null,
    assetsRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    selection: new Map<string, LicenseUser>(),
    selectAllClientOnlyValue: 'none',
    selectAllClientOnlyScope: 'page',
    vehiclesHistoryRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    fleetInformationMap: new Map<string, FleetVehicleInformation>(),
    smsStatusesMap: new Map<string, SmsInviteStatus>(),
    filterByAssetIds: [],
    historicalNotifications: [],
    linkedVehicleMap: new Map<string, DriverVehicleLink>(),
    linkedDriverMap: new Map<string, VehicleDriverLink>(),
    singleAssetDriverData: null,
    importResponse: new AssetsImportResponse(),
    copilotVersions: [],
    formattedMapVersions: new Map<string, string>(),
    selectAllJobTraceId: null,
  }),
  withEntities<LicenseUser, 'entityId'>({ idKey: 'entityId' }),
  withRequestsStatus<
    | 'asset-import'
    | 'fleet-information'
    | 'linked-data'
    | 'load-assets'
    | 'load-historical-notifications'
    | 'load-initial-selection'
    | 'samsara-invite'
    | 'select-all-bulk-action'
    | 'single-asset-driver-data'
    | 'sms-information'
  >()
);

export const trackAssetsRequests = <T>(keys: RequestKeys[]) =>
  trackRequest<T, RequestKeys>(keys);

export const joinAssetsRequests = <T>(keys: RequestKeys[]) =>
  joinRequestResult<T>(keys);

export const fetchAssetsCsvRequest$ = assetStore.pipe(
  joinAssetsRequests(['fetch-csv']),
  map(({ status, fetchStatus, isSuccess }) => ({
    status,
    fetchStatus,
    isSuccess,
  })),
  distinctUntilChanged()
);

export const trackAssetsRequestsStatus =
  createRequestsStatusOperator(assetStore);

export const assetStoreSnapshot = () => assetStore.getValue();

export const loadAssetsComplete$ = assetStore.pipe(
  selectRequestStatus('load-assets'),
  filter(({ value }) => value === 'error' || value === 'success')
);

export const loading$ = combineLatest([
  assetStore.pipe(selectIsRequestPending('load-assets')),
  assetStore.pipe(selectIsRequestPending('load-initial-selection')),
]).pipe(
  map(
    ([loadingData, loadingInitialSelection]) =>
      loadingData || loadingInitialSelection
  )
);

export const fleetInformationStatus$ = assetStore.pipe(
  selectRequestStatus('fleet-information')
);

export const selectAllBulkActionStatus$ = assetStore.pipe(
  selectRequestStatus('select-all-bulk-action')
);

export const smsInformationStatus$ = assetStore.pipe(
  selectRequestStatus('sms-information')
);

export const linkedDataStatus$ = assetStore.pipe(
  selectRequestStatus('linked-data')
);

export const samsaraInviteStatus$ = assetStore.pipe(
  selectRequestStatus('samsara-invite')
);

export const singleAssetDriverDataStatus$ = assetStore.pipe(
  selectRequestStatus('single-asset-driver-data')
);

export const hasAssets$ = assetStore.pipe(select(({ hasAssets }) => hasAssets));

export const hasAssignedLicenses$ = assetStore.pipe(
  selectAllEntities(),
  map((assets) => assets.some((asset) => asset.assignments.length > 0))
);

export const hasInvitedDrivers$ = assetStore.pipe(
  selectAllEntities(),
  map((assets) =>
    assets.some((asset) =>
      asset.assignments.some(
        (assignment) =>
          Boolean(assignment.onboardEmailSent) ||
          Boolean(assignment.onboardSMSSent)
      )
    )
  ),
  distinctUntilChanged()
);

export const assetsRowsDescriptor$ = assetStore.pipe(
  select(({ assetsRowsDescriptor }) => assetsRowsDescriptor)
);

const selectedAssetsSource$ = new BehaviorSubject<LicenseUser[]>([]);
export const selectedAssets$ = selectedAssetsSource$.asObservable();

export const allAssetsSelected$ = assetStore.pipe(
  select(
    ({ selectAllClientOnlyValue, selectAllClientOnlyScope }) =>
      selectAllClientOnlyValue === 'all' &&
      selectAllClientOnlyScope === 'dataset'
  )
);

export const totalAssets$ = assetStore.pipe(
  select(({ totalAssets }) => totalAssets)
);

export const filterAssetsSelectOption$ = assetStore.pipe(
  select(({ filterAssetsSelectOption }) => filterAssetsSelectOption)
);

export const vehiclesHistoryRowsDescriptor$ = assetStore.pipe(
  select(({ vehiclesHistoryRowsDescriptor }) => vehiclesHistoryRowsDescriptor)
);

export const loadingHistoricalNotifications$ = assetStore.pipe(
  selectIsRequestPending('load-historical-notifications')
);

export const didLoadingHistoricalNotificationsError$ = assetStore.pipe(
  selectRequestStatus('load-historical-notifications'),
  map(({ value }) => value === 'error')
);

export const historicalNotifications$ = assetStore.pipe(
  select(({ historicalNotifications }) => historicalNotifications)
);

export const notificationRows$ = combineLatest([
  loadingHistoricalNotifications$,
  historicalNotifications$,
]).pipe(
  map(([loading, notifications]) =>
    loading
      ? new LoadingRowsDescriptor()
      : notifications.length === 0
        ? EMPTY_ROWS_DESCRIPTOR
        : new UpdateRowsDescriptor<HistoricalNotification, number>(
            notifications.map(
              (notification, index) =>
                new RowDescriptor(notification, () => index)
            ),
            0,
            notifications.length,
            0,
            SelectAllState.Deselected
          )
  )
);

export const importResponse$ = assetStore.pipe(
  select((state) => state.importResponse)
);

export const singleAssetDriverData$ = assetStore.pipe(
  select(({ singleAssetDriverData }) => singleAssetDriverData)
);

export const copilotVersions$ = assetStore.pipe(
  select(({ copilotVersions }) => copilotVersions)
);

export const formattedMapVersions$ = assetStore.pipe(
  select(({ formattedMapVersions }) => formattedMapVersions)
);

export const selectAllJobTraceId$ = assetStore.pipe(
  select(({ selectAllJobTraceId }) => selectAllJobTraceId)
);

export function updateSamsaraInviteStatus() {
  assetStore.update(updateRequestStatus('samsara-invite', 'success'));
}

export function loadingSnapshot() {
  const loadingData =
    assetStore.query(getRequestStatus('load-assets')).value === 'pending';
  const loadingInitialSelection =
    assetStore.query(getRequestStatus('load-initial-selection')).value ===
    'pending';
  return loadingData || loadingInitialSelection;
}

export function updateAssets(
  assets: LicenseUser[],
  totalAssets: number,
  hasAssets: boolean
) {
  assetStore.update(
    (state) => ({
      ...state,
      totalAssets,
      hasAssets,
    }),
    setEntities(assets)
  );
}

export function updateLoadingAssetsStatus(loading: boolean) {
  assetStore.update(
    updateRequestStatus('load-assets', loading ? 'pending' : 'success')
  );
  dispatch(refreshAssetsRowsDescriptor());
}

export function updateFleetInformationMap(
  fleetInformation: FleetVehicleInformation[]
) {
  const { fleetInformationMap } = assetStore.getValue();
  for (const fleet of fleetInformation) {
    fleetInformationMap.set(fleet.deviceId, fleet);
  }
  assetStore.update(
    (state) => ({
      ...state,
      fleetInformationMap,
    }),
    updateRequestStatus('fleet-information', 'success')
  );
}

export function updateSmsStatusesMap(smsStatuses: SmsInviteStatus[]) {
  const { smsStatusesMap } = assetStore.getValue();
  for (const smsStatus of smsStatuses) {
    smsStatusesMap.set(smsStatus.entityId, smsStatus);
  }
  assetStore.update(
    (state) => ({
      ...state,
      smsStatusesMap,
    }),
    updateRequestStatus('sms-information', 'success')
  );
}

export function updateLinkedVehicleMap(linkedData: DriverVehicleLink[]) {
  const linkedVehicleMap = assetStore.getValue().linkedVehicleMap;
  for (const data of linkedData) {
    linkedVehicleMap.set(data.driverId, data);
  }
  assetStore.update(
    (state) => ({
      ...state,
      linkedVehicleMap,
    }),
    updateRequestStatus('linked-data', 'success')
  );
}

export function updateLinkedDriverMap(linkedData: VehicleDriverLink[]) {
  const linkedDriverMap = assetStore.getValue().linkedDriverMap;
  for (const data of linkedData) {
    linkedDriverMap.set(data.vehicleDriverLinks[0].vehicleId, data);
  }
  assetStore.update(
    (state) => ({
      ...state,
      linkedDriverMap,
    }),
    updateRequestStatus('linked-data', 'success')
  );
}

export function updateOffset(offset: number) {
  assetStore.update((state) => ({
    ...state,
    offset,
  }));
}

export function updateSort(
  sortExpression: string,
  sortDirection: '' | 'asc' | 'desc'
) {
  assetStore.update((state) => ({
    ...state,
    sortExpression,
    sortDirection,
  }));
}

export function updateQuery(query: string) {
  assetStore.update((state) => ({
    ...state,
    query,
  }));
}

export function updateFilterAssetsSelectOption(
  filterAssetsSelectOption: FilterAssetsSelectOption | null
) {
  assetStore.update((state) => ({
    ...state,
    filterAssetsSelectOption,
  }));
}

export function updateAssetsRowsDescriptor(
  assetsRowsDescriptor: RowsDescriptor
) {
  assetStore.update((state) => ({
    ...state,
    assetsRowsDescriptor,
  }));
}

export function updateVehiclesHistoryRowsDescriptor(
  vehiclesHistoryRowsDescriptor: RowsDescriptor
) {
  assetStore.update((state) => ({
    ...state,
    vehiclesHistoryRowsDescriptor,
  }));
}

export function clearAssetsStore() {
  emitOnce(() => {
    assetStore.update(clearRequestsStatus());
    assetStore.reset();
  });
}

export const assetDeleted$ = new Subject<void>();
export const assetCreatedOrUpdated$ = new Subject<void>();

export function updateSelectedAssets() {
  const { selection } = assetStoreSnapshot();
  selectedAssetsSource$.next(Array.from(selection.values()));
}

export function updateFilterByAssets(filterByAssetIds: string[]) {
  assetStore.update((state) => ({
    ...state,
    filterByAssetIds,
  }));
}

export function updateHistoricalNotifications(
  historicalNotifications: HistoricalNotification[],
  failed = false
) {
  assetStore.update(
    (state) => ({
      ...state,
      historicalNotifications,
    }),
    failed
      ? updateRequestStatus('load-historical-notifications', 'error', null)
      : updateRequestStatus('load-historical-notifications', 'success')
  );
}

export function updateAssetImportStatus() {
  assetStore.update(updateRequestStatus('asset-import', 'success'));
}

export function updateImportResponse(importResponse: AssetsImportResponse) {
  assetStore.update((state) => ({
    ...state,
    importResponse,
  }));
}

export function updateAssetDeleted() {
  assetDeleted$.next();
}

export function updateAssetCreatedOrUpdated() {
  assetCreatedOrUpdated$.next();
}

export function updateCopilotVersions(copilotVersions: string[]) {
  assetStore.update((state) => ({
    ...state,
    copilotVersions,
  }));
}

export function updateFormattedMapVersions(
  formattedMapVersions: Map<string, string>
) {
  assetStore.update((state) => ({
    ...state,
    formattedMapVersions,
  }));
}

export function updateSelectAllClientOnly(
  selectAllClientOnlyValue: ClientOnlyValue,
  selectAllClientOnlyScope: ClientOnlyScope
) {
  assetStore.update((state) => ({
    ...state,
    selectAllClientOnlyValue,
    selectAllClientOnlyScope,
  }));
}

export function updateSelectAllBulkActionStatus(
  status: 'error' | 'pending' | 'success',
  error?: string
) {
  if (status === 'error') {
    assetStore.update(
      updateRequestStatus('select-all-bulk-action', status, error)
    );
  } else {
    assetStore.update(updateRequestStatus('select-all-bulk-action', status));
  }
}

export function updateSelectAllJobTraceId(selectAllJobTraceId: null | string) {
  assetStore.update((state) => ({
    ...state,
    selectAllJobTraceId,
  }));
}

export function updateSingleAssetDriverData(
  singleAssetDriverData: null | VehicleDriverLink
) {
  assetStore.update(
    (state) => ({
      ...state,
      singleAssetDriverData,
    }),
    updateRequestStatus('single-asset-driver-data', 'success')
  );
}

export const assetStoreEntities = () => assetStore.query(getAllEntities());
